import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

// Legal pages — concise standard copy. TESSA's team can replace these texts
// with their official legal versions at any time.
const DOCS = {
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    updated: "Last updated: October 2026",
    sections: [
      {
        h: "What we collect",
        p: "When you send us a contact or order request we store the details you provide: your name, business email, company, country, business type and message. The site also records anonymous usage statistics (pages visited, clicks and catalog searches) that cannot identify you personally.",
      },
      {
        h: "Favorites and your device",
        p: "Your favorite varieties and your order selection are stored only in your own browser (local storage). They never leave your device until you choose to send an order request.",
      },
      {
        h: "How we use your information",
        p: "We use your contact details exclusively to respond to your enquiry and to prepare a commercial proposal. We do not sell, rent or share your personal information with third parties for their marketing purposes.",
      },
      {
        h: "How long we keep it",
        p: "Enquiries are kept while your commercial conversation with us is active. You can ask us at any time to correct or delete your information by writing to our sales team through the contact form.",
      },
      {
        h: "Analytics",
        p: "We use Google Analytics 4 to understand which pages and varieties interest our visitors. It runs with IP anonymization and, by default, without analytics cookies. We also keep our own anonymous counters of page visits, clicks and catalog searches.",
      },
      {
        h: "Security",
        p: "Access to submitted enquiries is restricted to authorized TESSA personnel through an authenticated administration panel.",
      },
    ],
  },
  terms: {
    eyebrow: "Legal",
    title: "Terms & Conditions",
    updated: "Last updated: October 2026",
    sections: [
      {
        h: "About this site",
        p: "This website presents TESSA Corporation, an Ecuadorian flower grower and exporter, and its catalog of varieties. The content is informational and does not constitute a binding commercial offer.",
      },
      {
        h: "Order requests",
        p: "The order request feature lets you send a list of varieties and quantities to our sales team. It is not a purchase: no payment is processed on this site, and no contract exists until both parties confirm availability, prices, units and logistics in writing.",
      },
      {
        h: "Catalog information",
        p: "We work to keep variety descriptions, photos and availability accurate, but they may change without notice. Variety names are trademarks or commercial names of their respective breeders.",
      },
      {
        h: "Intellectual property",
        p: "All texts, photographs, logos and design elements on this site belong to TESSA Corporation or are used with permission. They may not be reproduced without written authorization.",
      },
      {
        h: "Contact",
        p: "For any question about these terms, reach us through the contact form on the home page.",
      },
    ],
  },
};

export default function LegalPage({ doc }) {
  const { t } = useLanguage();
  const d = DOCS[doc] || DOCS.privacy;
  return (
    <main className="min-h-screen bg-[#F6F2EC] text-[#252525] pt-28 pb-24" data-testid={`legal-${doc}`}>
      <div className="container-tessa max-w-3xl">
        <Link to="/" className="flex w-fit items-center gap-2 text-[12px] uppercase tracking-[0.16em] text-[#817B74] hover:text-[#D92A47] transition-colors mb-10">
          <ArrowLeft size={14} /> {t("pages.backHome")}
        </Link>
        <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">{d.eyebrow}</span>
        <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] mt-3">{d.title}</h1>
        <p className="text-[#817B74] text-sm mt-3 mb-12">{d.updated}</p>
        <div className="space-y-10">
          {d.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-serif text-2xl text-[#252525] mb-2">{s.h}</h2>
              <p className="text-[#5f5a54] leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
